import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import Pagination from '@material-ui/lab/Pagination';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { Button as ChakraButton, IconButton } from '@chakra-ui/react';
import { FaHistory, FaPlusCircle, FaSearch, FaEdit, FaTimesCircle } from 'react-icons/fa';

import { ApplicationState } from '../../../store';
import { CompanyMaster } from '../../../models/CompanyMaster';
import { useAdminContext } from '../../../context/adminProvider';
import { logAdminAction } from '../../../helpers/adminLog';
import AdminActionLogModal from '../../log/AdminActionLogModal';

interface AdminRecord {
    citizen_id: string;
    sid: string;
    role_access: string;
}

interface AdminResponse {
    total_count: number;
    result_list: AdminRecord[];
}

interface SelectOption {
    value: string;
    label: string;
}

const ROLE_OPTIONS = ['admin', 'super_admin'];
const EMPTY_FORM: AdminRecord = { citizen_id: '', sid: '', role_access: 'admin' };

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

function AdminManagement() {
    const [records, setRecords] = useState<AdminRecord[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [form, setForm] = useState<AdminRecord>(EMPTY_FORM);
    const [modalUsers, setModalUsers] = useState<SelectOption[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLogOpen, setIsLogOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [searchCitizenId, setSearchCitizenId] = useState('');
    const [searchSid, setSearchSid] = useState('');
    const [searchRole, setSearchRole] = useState<SelectOption | null>(null);
    const [activeFilter, setActiveFilter] = useState({ citizenId: '', sid: '', role: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const companies: CompanyMaster[] = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );

    const { userRole } = useAdminContext();

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies
            .filter(c => allowedSids.has(c.sid))
            .map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;
    const isSuperAdmin = userRole.some(r => r.roleAccess === 'super_admin');
    const availableRoleOptions = isSuperAdmin ? ROLE_OPTIONS : ['admin'];

    const roleSelectOptions: SelectOption[] = ROLE_OPTIONS.map(r => ({ value: r, label: r }));

    const findCompanyName = useCallback((sid: string): string => {
        if (sid === '*') return '* (ทุกบริษัท)';
        const found = companies.find(c => c.sid === sid);
        return found ? found.company_name : sid;
    }, [companies]);

    const token = () => localStorage.getItem('DMS_APPLICATION') ?? '';

    const fetchAll = useCallback(async () => {
        try {
            const res = await fetch('/v1/admin/all', {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) throw await res.json();
            const data: AdminResponse = await res.json();
            setRecords(data.result_list ?? []);
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'โหลดข้อมูลไม่สำเร็จ', 'error');
        }
    }, []);

    useEffect(() => {
        if (companyOptions.length === 1) {
            setSearchSid(companyOptions[0].value);
            setActiveFilter(prev => ({ ...prev, sid: companyOptions[0].value }));
        }
    }, [companyOptions]);

    const filteredRecords = useMemo(() => {
        const { citizenId, sid, role } = activeFilter;
        return records.filter(r => {
            if (citizenId && !r.citizen_id.toLowerCase().includes(citizenId.toLowerCase())) return false;
            if (sid && r.sid !== sid) return false;
            if (role && r.role_access !== role) return false;
            return true;
        });
    }, [records, activeFilter]);

    const totalPages = Math.ceil(filteredRecords.length / pageSize);
    const pagedRecords = filteredRecords.slice((page - 1) * pageSize, page * pageSize);

    const handleSearch = async () => {
        await fetchAll();
        setActiveFilter({
            citizenId: searchCitizenId,
            sid: searchSid,
            role: searchRole?.value ?? '',
        });
        setPage(1);
        setHasSearched(true);
    };

    const fetchUsersBySid = useCallback(async (sid: string) => {
        if (!sid) { setModalUsers([]); return; }
        setIsLoadingUsers(true);
        try {
            const res = await fetch(`/v1/user?sid=${sid}&id=&role_access=&page=1&page_size=10000`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` }
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setModalUsers((data.result_list ?? []).map((emp: { id: string }) => ({ value: emp.id, label: emp.id })));
        } catch {
            setModalUsers([]);
        } finally {
            setIsLoadingUsers(false);
        }
    }, []);

    const openCreate = () => {
        const sid = isCompanyLocked ? companyOptions[0].value : '';
        setForm({ ...EMPTY_FORM, sid });
        setIsEdit(false);
        setIsOpen(true);
        if (sid) fetchUsersBySid(sid);
        else setModalUsers([]);
    };

    const openEdit = (record: AdminRecord) => {
        setForm({ ...record });
        setIsEdit(true);
        setIsOpen(true);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleConfirm = async () => {
        if (!form.citizen_id.trim() || !form.sid.trim()) {
            Swal.fire('กรุณากรอกข้อมูลให้ครบ', '', 'warning');
            return;
        }
        try {
            const res = await fetch('/v1/admin', {
                method: isEdit ? 'PUT' : 'POST',
                headers: {
                    Authorization: `Bearer ${token()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw await res.json();
            const data: AdminResponse = await res.json();
            setRecords(data.result_list ?? []);
            setIsOpen(false);
            logAdminAction('ADM-DMS101', isEdit ? 'UPDATE' : 'CREATE', form.citizen_id, form.sid, `role=${form.role_access}`);
            Swal.fire({ title: 'Success', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            const msg = (err as any).message ?? '';
            const isduplicate = msg.includes('23505') || msg.toLowerCase().includes('duplicate');
            Swal.fire('Error', isduplicate ? 'มีข้อมูลซ้ำอยู่ในระบบแล้ว กรุณาตรวจสอบ User และ บริษัท' : msg || 'บันทึกไม่สำเร็จ', 'error');
        }
    };

    const handleDelete = (record: AdminRecord) => {
        Swal.fire({
            title: 'ยืนยันการลบ?',
            text: `${record.citizen_id} / ${findCompanyName(record.sid)}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const res = await fetch(
                    `/v1/admin?citizen_id=${encodeURIComponent(record.citizen_id)}&sid=${encodeURIComponent(record.sid)}`,
                    { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }
                );
                if (!res.ok) throw await res.json();
                const data: AdminResponse = await res.json();
                setRecords(data.result_list ?? []);
                logAdminAction('ADM-DMS101', 'DELETE', record.citizen_id, record.sid);
                Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', (err as any).message ?? 'ลบไม่สำเร็จ', 'error');
            }
        });
    };

    return (
        <>
            <div className="mt-2">
                <div className="trapezoid" style={{ width: 'auto', display: 'inline-block' }}>
                    <span className="metadata-font">ADM-DMS101 Admin Management</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <ChakraButton
                        colorScheme="purple"
                        size="sm"
                        leftIcon={<FaHistory />}
                        className="mx-1"
                        onClick={() => setIsLogOpen(true)}
                    >
                        Log
                    </ChakraButton>
                    <ChakraButton
                        colorScheme="green"
                        size="sm"
                        leftIcon={<FaPlusCircle />}
                        className="mx-1"
                        onClick={openCreate}
                    >
                        Create
                    </ChakraButton>
                </div>
            </div>

            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>Search Menu</span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <Row>
                                <Col xs={3}>
                                    <Form.Group className="mb-3" controlId="searchCitizenId">
                                        <Form.Label>User :</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter User"
                                            value={searchCitizenId}
                                            onChange={e => setSearchCitizenId(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={5}>
                                    <Form.Group className="mb-3" controlId="searchCompany">
                                        <Form.Label>บริษัท :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก บริษัท"
                                            options={companyOptions}
                                            value={companyOptions.find(c => c.value === searchSid) ?? null}
                                            onChange={opt => setSearchSid(opt?.value ?? '')}
                                            isClearable={!isCompanyLocked}
                                            isDisabled={isCompanyLocked}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4}>
                                    <Form.Group className="mb-3" controlId="searchRole">
                                        <Form.Label>Role :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก Role"
                                            options={roleSelectOptions}
                                            value={searchRole}
                                            onChange={opt => setSearchRole(opt as SelectOption | null)}
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <ChakraButton
                                        leftIcon={<FaSearch />}
                                        colorScheme="green"
                                        size="sm"
                                        style={{ float: 'right' }}
                                        onClick={handleSearch}
                                    >
                                        ค้นหา
                                    </ChakraButton>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Col>
                </Row>

                <br />

                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0 align-items-center">
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>Show&nbsp;&nbsp;</label>
                            <select
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>&nbsp;&nbsp;entries</label>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <Pagination
                                count={totalPages}
                                shape="rounded"
                                showFirstButton
                                showLastButton
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mt-2">
                    <Col>
                        <table className="table table-bordered align-middle employee-font font-size-employee">
                            <thead>
                                <tr className="text-center table-active thead-light">
                                    <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Action</th>
                                    <th>User</th>
                                    <th>บริษัท</th>
                                    <th>Role</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!hasSearched ? (
                                    <tr>
                                        <td colSpan={4} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>กรุณากด ค้นหา เพื่อแสดงข้อมูล</td>
                                    </tr>
                                ) : pagedRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>No Data.</td>
                                    </tr>
                                ) : (
                                    pagedRecords.map((rec, i) => (
                                        <tr key={i}>
                                            <td className="text-center">
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme="blue"
                                                        size="sm"
                                                        icon={<FaEdit />}
                                                        aria-label="Edit"
                                                        onClick={() => openEdit(rec)}
                                                    />
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme="red"
                                                        size="sm"
                                                        icon={<FaTimesCircle />}
                                                        aria-label="Delete"
                                                        onClick={() => handleDelete(rec)}
                                                    />
                                                </div>
                                            </td>
                                            <td>{rec.citizen_id}</td>
                                            <td>{findCompanyName(rec.sid)}</td>
                                            <td className="text-center">
                                                <span className={`badge ${rec.role_access === 'super_admin' ? 'badge-danger' : 'badge-primary'}`}>
                                                    {rec.role_access}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </Col>
                </Row>
            </div>

            <AdminActionLogModal
                isOpen={isLogOpen}
                onClose={() => setIsLogOpen(false)}
                sourceModule="ADM-DMS101"
                defaultSid={searchSid}
            />

            <Modal isOpen={isOpen} toggle={() => setIsOpen(false)}>
                <ModalHeader toggle={() => setIsOpen(false)}>
                    {isEdit ? 'Edit Admin' : 'Create Admin'}
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>บริษัท</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก บริษัท"
                            options={companyOptions}
                            value={companyOptions.find(c => c.value === form.sid) ?? null}
                            onChange={opt => {
                                const sid = opt?.value ?? '';
                                setForm(prev => ({ ...prev, sid, citizen_id: '' }));
                                fetchUsersBySid(sid);
                            }}
                            isDisabled={isEdit || isCompanyLocked}
                            isClearable={!isCompanyLocked && !isEdit}
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>User</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก User"
                            options={modalUsers}
                            value={modalUsers.find(u => u.value === form.citizen_id) ?? null}
                            onChange={opt => setForm(prev => ({ ...prev, citizen_id: opt?.value ?? '' }))}
                            isDisabled={isEdit || !form.sid}
                            isLoading={isLoadingUsers}
                            isClearable={!isEdit}
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>Role</label>
                        <select
                            className="form-control"
                            name="role_access"
                            value={form.role_access}
                            onChange={handleChange}
                            disabled={!isSuperAdmin}
                        >
                            {availableRoleOptions.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleConfirm}>
                        {isEdit ? 'Save' : 'Create'}
                    </Button>
                    <ChakraButton colorScheme="red" onClick={() => setIsOpen(false)}>Cancel</ChakraButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default AdminManagement;
